import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import edgeMftService, { type AuthCredentials, type LoginResult } from '../services/edgeMftService';

interface User {
  username: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (credentials: AuthCredentials) => Promise<LoginResult>;
  verifyOTP: (sessionToken: string, otpCode: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    if (edgeMftService.isAuthenticated()) {
      const storedUser = edgeMftService.getCredentials();
      if (storedUser) {
        setUser({ username: storedUser.username });
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials: AuthCredentials): Promise<LoginResult> => {
    const result = await edgeMftService.authenticate(credentials);

    // Only set user if OTP is not required
    if (!result.requires_otp) {
      setUser({ username: credentials.username });
    }

    return result;
  };

  const verifyOTP = async (sessionToken: string, otpCode: string) => {
    await edgeMftService.verifyOTP(sessionToken, otpCode);

    // Get username from stored user data after OTP verification
    const storedUser = edgeMftService.getCredentials();
    if (storedUser) {
      setUser({ username: storedUser.username });
    }
  };

  const logout = () => {
    edgeMftService.logout();
    setUser(null);
  };

  const value = {
    isAuthenticated: !!user,
    user,
    login,
    verifyOTP,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};